<template>
  <b-tooltip :label="tooltipLabel"
    class="width-100percent"
    :position="position"
    v-if='!isDetail'>
    <qd-taginput
      v-model="itemsSelected"
      :data="filteredItems"
      :loading="isLoad"
      :open-on-focus="true"
      allow-new
      autocomplete
      :confirm-keys="confirmKeys"
      :before-adding="beforeAdding"
      field="libelle"
      type="is-primary"
      icon="tag-plus-outline"
      @focus="onFocus"
      @typing="inTyping"
      @add="gestionNewItem"
      @remove="setItems"
      ref="itemsTags">
      <template #selected="{ tags }">
        <b-tag
          :style="{ backgroundColor: tag.couleur }"
          v-for="(tag, index) in tags" :key="index"
          @close="$refs.itemsTags.removeTag(index, $event)"
          :type="chooseTagType(tag)"
          closable>
          {{getValue(tag)}}
        </b-tag>
      </template>
      <template slot="empty">
        <div v-if="!isLoad">
          {{$t('general.autocomplete.noRslt')}}
        </div>
        <div v-else>{{$t('general.autocomplete.search')}}</div>
      </template>
    </qd-taginput>
  </b-tooltip>
  <b-taglist v-else>
    <b-tag v-for="(tag, index) in itemsSelected"
      :key="index"
      :style="{ backgroundColor: tag.couleur }"
      :type="chooseTagType(tag)"
      size="is-medium">
      <b-tooltip v-if="!tag.actif"
                 :label="$t('detailCampagne.informations.tagArchive')"
                 position="is-top"
      >
        {{getValue(tag)}}
      </b-tooltip>
      <span v-else>
        {{ getValue(tag) }}
      </span>
    </b-tag>
  </b-taglist>
</template>

<script>
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import getValueByPath from 'lodash/get';
import messageErreur from '@/services/general/messageErreur';
import { create, getAll } from '@/api/tag/index';
import genererTag from '@/services/parametrage/generation/genererTag';
import { CAMPAGNE } from '@/configuration/parametrage/tagConf';
import { chooseTagType } from '@/services/parametrage/tag/tagService';
import messageSucces from '@/services/general/messageSucces';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';

const Parametres = createNamespacedHelpers('parametres');

export default {
  props: {
    value: {
      type: Array,
      required: true,
    },
    field: {
      type: String,
      required: true,
    },
    moduleTag: {
      type: String,
      required: false,
      default: CAMPAGNE,
    },
    isDetail: {
      type: Boolean,
      required: false,
      default: false,
    },
    tooltipLabel: {
      type: String,
      required: false,
      default: '',
    },
    position: {
      type: String,
      required: false,
      default: 'is-left',
    },
  },
  data() {
    return {
      filteredItems: [],
      itemsSelected: [],
      isLoad: false,
      itemsBase: [],
      confirmKeys: ['Tab', 'Enter'],
    };
  },
  computed: {
    items: {
      get() {
        return this.value ?? [];
      },
      set(newValue) {
        this.$emit('input', newValue);
      },
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametreForce: 'loadParametreForce',
    }),
    chooseTagType,
    setItems() {
      const list = this.itemsBase.filter(item => this.itemsSelected
        .map(tag => tag.id).includes(item.tagId));
      this.itemsSelected.forEach((el) => {
        if (!list.map(item => item.tagId).includes(el.id)) {
          list.push({
            tagId: el.id,
            tag: {
              couleur: el.couleur,
              libelle: el.libelle,
              actif: el.actif,
              id: el.id,
            },
          });
        }
      });
      this.items = cloneDeep(list);
    },
    async gestionNewItem(value) {
      if (typeof value === 'object') {
        this.setItems();
        return;
      }
      this.isLoad = true;
      try {
        let result = {};
        // vérifier si la valeur saisie existe
        const response = (await getAll({
          query: {
            auto: value,
            actif: true,
            moduleTag: this.moduleTag,
          },
        })).data.result.items;
        result = response.find(el => el.libelle.toLowerCase() === value.toLowerCase());

        // sinon créer
        if (!result) {
          const toCreate = genererTag();
          toCreate.libelle = value;
          toCreate.moduleTag = this.moduleTag;
          result = (await create(toCreate)).data.result;
          await this.loadParametreForce(PARAMETRES_KEYS.tag);
          messageSucces(this.$t('editionCampagne.messages.infoAjoutTagSucess'));
        }
        const index = this.itemsSelected.indexOf(value);
        if (index !== -1) this.itemsSelected.splice(index, 1, result);
        else this.itemsSelected.push(result);
        this.setItems();
      } catch (e) {
        messageErreur(this.$i18n);
        throw (e);
      } finally {
        this.isLoad = false;
      }
    },
    onFocus() {
      this.getItemsData();
    },
    beforeAdding(value) {
      return !this.itemsSelected.some((item) => {
        if (typeof value === 'object') return item.id === value.id;
        return item.libelle.toLowerCase() === value.toLowerCase();
      });
    },
    inTyping(search) {
      if (!search) return;
      this.filteredItems = [];
      this.getItemsData(search);
    },
    getItemsData: debounce(async function loadData(search) {
      this.isLoad = true;
      try {
        const results = (await getAll({
          query: {
            auto: search,
            actif: true,
            moduleTag: this.moduleTag,
          },
        })).data.result.items;
        this.filteredItems = results.filter((item) => {
          return !this.itemsSelected.map(i => i.id).includes(item.id);
        });
      } catch (e) {
        messageErreur(this.$i18n);
        throw (e);
      } finally {
        this.isLoad = false;
      }
    }, 300),
    getValue(obj) {
      return getValueByPath(obj, 'libelle');
    },
  },
  mounted() {
    this.itemsBase = cloneDeep(this.items);
    if (this.items.length) {
      this.itemsSelected = this.items.map(el => el[this.field]);
    }
  },
};
</script>
